import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import { Button, Card, Chip, Divider, Paragraph, Title, useTheme } from 'react-native-paper';
import type { WeatherForecast } from '../types';
import { FISHING_DAY_START_HOUR, getFishingDayForecasts, getForecastHour } from '../utils/forecastHours';
import { hasMarineData } from '../utils/marineData';
import { getSeaState } from '../utils/seaState';

interface MarineForecastBriefProps {
  dailyForecast: WeatherForecast[];
  hourlyForecast: WeatherForecast[];
  initialDate?: string;
  showHeader?: boolean;
  showDateSelector?: boolean;
}

type RiskLevel = 'favorable' | 'caution' | 'high';

const TIMELINE_CARD_WIDTH = 154;
const TIMELINE_CARD_GAP = 8;

interface Assessment {
  level: RiskLevel;
  label: string;
  color: string;
  summary: string;
  factors: string[];
}

const direction = (degrees: number): string => {
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return directions[Math.round((((degrees % 360) + 360) % 360) / 45) % 8];
};

const dateKey = (forecast: WeatherForecast): string => forecast.timestamp.slice(0, 10);

const hourLabel = (forecast: WeatherForecast): string => {
  const hour = getForecastHour(forecast.timestamp);
  if (hour === 0) return '12AM';
  if (hour === 12) return '12PM';
  return `${hour % 12}${hour < 12 ? 'AM' : 'PM'}`;
};

const average = (values: number[]): number => values.reduce((total, value) => total + value, 0) / values.length;

const hourlyRisk = (forecast: WeatherForecast): number => {
  let score = 0;
  const gust = forecast.wind.gusts ?? forecast.wind.speed;
  const condition = forecast.weather.conditions.toLowerCase();
  if (gust >= 30) score += 4;
  else if (gust >= 22) score += 2;
  else if (gust >= 18) score += 1;
  if (hasMarineData(forecast.waves)) {
    if (forecast.waves.height >= 7) score += 4;
    else if (forecast.waves.height >= 5) score += 3;
    else if (forecast.waves.height >= 3) score += 1;
    if (forecast.waves.height >= 2 && forecast.waves.period <= 5) score += 2;
  }
  if (condition.includes('thunder')) score += 4;
  else if (condition.includes('rain') && forecast.weather.precipitation >= 1) score += 1;
  return score;
};

const assessDay = (hours: WeatherForecast[]): Assessment => {
  if (!hours.length) {
    return { level: 'caution', label: 'Forecast unavailable', color: '#B45309', summary: 'Hourly sea-state data is not available for this date.', factors: ['Check the latest official marine forecast before departure.'] };
  }
  const marineHours = hours.filter(hour => hasMarineData(hour.waves));
  const averageGust = average(hours.map(hour => hour.wind.gusts ?? hour.wind.speed));
  const averageWave = marineHours.length ? average(marineHours.map(hour => hour.waves.height)) : null;
  const shortPeriodHours = marineHours.filter(hour => hour.waves.height >= 2 && hour.waves.period <= 5).length;
  const thunderHours = hours.filter(hour => hour.weather.conditions.toLowerCase().includes('thunder')).length;
  const score = average(hours.map(hourlyRisk));
  const factors: string[] = [];
  if (averageGust >= 18) factors.push(`Average gusts are around ${Math.round(averageGust)} kt.`);
  if (averageWave !== null && averageWave >= 3) factors.push(`Average seas are around ${Math.round(averageWave * 10) / 10} ft.`);
  if (shortPeriodHours > 0) factors.push(`${shortPeriodHours} hourly period${shortPeriodHours === 1 ? '' : 's'} may have steeper, short-period waves.`);
  if (thunderHours > 0) factors.push(`Thunderstorms are possible during ${thunderHours} hourly period${thunderHours === 1 ? '' : 's'}; monitor radar and official warnings.`);
  if (marineHours.length < hours.length) factors.push(
    marineHours.length
      ? `Marine guidance covers ${marineHours.length} of ${hours.length} daytime hours; uncovered hours are excluded.`
      : 'Marine model guidance is unavailable for these daytime hours.'
  );
  if (!factors.length) factors.push('Daytime wind and sea state remain relatively light in this forecast.');

  if (!marineHours.length) return {
    level: 'caution',
    label: 'Marine guidance unavailable',
    color: '#B45309',
    summary: 'Wind and weather are available, but seas and swell cannot be assessed for this date.',
    factors,
  };

  if (score >= 5) return { level: 'high', label: 'High-risk daytime', color: '#B42318', summary: 'Daytime conditions may exceed the comfort margin of many recreational boats.', factors };
  if (score >= 2.5) return { level: 'caution', label: 'Use caution', color: '#B45309', summary: 'Daytime conditions warrant a plan matched to your vessel, crew, and route.', factors };
  return { level: 'favorable', label: 'Favorable daytime', color: '#087443', summary: 'Daytime conditions look manageable; use the timeline to spot brief changes.', factors };
};

const bestWindow = (hours: WeatherForecast[]): string => {
  let best: WeatherForecast[] = [];
  let active: WeatherForecast[] = [];
  hours.forEach(hour => {
    if (hasMarineData(hour.waves) && hourlyRisk(hour) <= 2) active.push(hour);
    else {
      if (active.length > best.length) best = active;
      active = [];
    }
  });
  if (active.length > best.length) best = active;
  if (!best.length) return 'No low-risk window identified';
  return `${hourLabel(best[0])}–${hourLabel(best[best.length - 1])}${best.length === 1 ? '' : ` (${best.length} hrs)`}`;
};

/** A voyage-planning brief based on hourly marine and weather model output. */
export function MarineForecastBrief({
  hourlyForecast,
  initialDate,
  showHeader = true,
  showDateSelector = true,
}: MarineForecastBriefProps): React.JSX.Element | null {
  const theme = useTheme();
  const timelineScrollRef = useRef<ScrollView>(null);
  const positionedTimelineKeyRef = useRef('');
  const dates = useMemo(() => Array.from(new Set(hourlyForecast.map(dateKey))), [hourlyForecast]);
  const [selectedDate, setSelectedDate] = useState(initialDate ?? dates[0] ?? '');
  const [showAllHours, setShowAllHours] = useState(false);

  useEffect(() => {
    // A forecast card has already established the user's selected date.
    // Never replace it with Today merely because hourly data is still loading.
    if (initialDate !== undefined) {
      setSelectedDate(initialDate);
    } else if (!dates.includes(selectedDate)) {
      setSelectedDate(dates[0] ?? '');
    }
  }, [dates, initialDate, selectedDate]);

  const hours = useMemo(() => hourlyForecast.filter(hour => dateKey(hour) === selectedDate), [hourlyForecast, selectedDate]);
  const fishingDayHours = useMemo(() => getFishingDayForecasts(hours), [hours]);
  const availableFishingDayHours = useMemo(
    () => fishingDayHours.filter(hour => hasMarineData(hour.waves)),
    [fishingDayHours]
  );
  const assessment = useMemo(() => assessDay(fishingDayHours), [fishingDayHours]);
  const timelineHours = useMemo(
    () => showAllHours ? hours : hours.filter(hour => getForecastHour(hour.timestamp) % 3 === 0),
    [hours, showAllHours]
  );
  const daytimeStartOffset = useMemo(() => {
    const daytimeStartIndex = timelineHours.findIndex(
      hour => getForecastHour(hour.timestamp) >= FISHING_DAY_START_HOUR
    );
    return Math.max(daytimeStartIndex, 0) * (TIMELINE_CARD_WIDTH + TIMELINE_CARD_GAP);
  }, [timelineHours]);
  const timelineKey = `${selectedDate}-${showAllHours ? 'hourly' : 'checkpoints'}`;

  useEffect(() => {
    setShowAllHours(false);
  }, [selectedDate]);

  if (!hourlyForecast.length) return null;

  return (
    <View style={styles.container}>
      {showHeader && (
        <View style={styles.sectionHeading}>
          <View>
            <Title style={styles.title}>Offshore planning brief</Title>
            <Paragraph style={styles.subtitle}>Hourly wind, seas, swell, and weather model guidance</Paragraph>
          </View>
          <MaterialCommunityIcons name="ship-wheel" size={28} color={theme.colors.primary} />
        </View>
      )}

      {showDateSelector && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.dateChips}>
          {dates.map((date) => {
            const parsed = new Date(`${date}T12:00:00`);
            return (
              <Chip
                key={date}
                selected={date === selectedDate}
                onPress={() => setSelectedDate(date)}
                style={[styles.dateChip, date === selectedDate && { backgroundColor: theme.colors.primaryContainer }]}
                textStyle={date === selectedDate ? { color: theme.colors.onPrimaryContainer } : undefined}
              >
                {date === dates[0] ? 'Today' : parsed.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
              </Chip>
            );
          })}
        </ScrollView>
      )}

      <Card style={[styles.assessmentCard, { borderLeftColor: assessment.color }]} mode="outlined">
        <Card.Content>
          <View style={styles.assessmentTop}>
            <View style={[styles.statusDot, { backgroundColor: assessment.color }]} />
            <View style={styles.assessmentCopy}>
              <Paragraph style={[styles.statusLabel, { color: assessment.color }]}>{assessment.label.toUpperCase()}</Paragraph>
              <Paragraph style={styles.assessmentSummary}>{assessment.summary}</Paragraph>
            </View>
          </View>
          <View style={styles.metricRow}>
            <View style={styles.metric}><Paragraph style={styles.metricLabel}>DAYTIME BEST WINDOW</Paragraph><Paragraph style={styles.metricValue}>{bestWindow(fishingDayHours)}</Paragraph></View>
            <View style={styles.metric}><Paragraph style={styles.metricLabel}>DAYTIME AVG SEAS</Paragraph><Paragraph style={styles.metricValue}>{availableFishingDayHours.length ? `${Math.round(average(availableFishingDayHours.map(hour => hour.waves.height)) * 10) / 10} ft` : '—'}</Paragraph></View>
            <View style={styles.metric}><Paragraph style={styles.metricLabel}>DAYTIME PEAK GUST</Paragraph><Paragraph style={styles.metricValue}>{fishingDayHours.length ? `${Math.round(Math.max(...fishingDayHours.map(hour => hour.wind.gusts ?? hour.wind.speed)))} kt` : '—'}</Paragraph></View>
          </View>
          <Divider style={styles.assessmentDivider} />
          {assessment.factors.map(factor => <Paragraph key={factor} style={styles.factor}>• {factor}</Paragraph>)}
        </Card.Content>
      </Card>

      <View style={styles.timelineHeader}>
        <View>
          <Title style={styles.hourlyTitle}>Marine timeline</Title>
          <Paragraph style={styles.timelineSubtitle}>{showAllHours ? 'All hourly checkpoints' : '3-hour checkpoints'} · Forecast-location time</Paragraph>
        </View>
        <Button compact mode="text" onPress={() => setShowAllHours(value => !value)}>
          {showAllHours ? '3-hour view' : 'All 24 hours'}
        </Button>
      </View>
      <ScrollView
        ref={timelineScrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.timelineScroll}
        onContentSizeChange={() => {
          if (!timelineHours.length || positionedTimelineKeyRef.current === timelineKey) return;
          timelineScrollRef.current?.scrollTo({ x: daytimeStartOffset, y: 0, animated: false });
          positionedTimelineKeyRef.current = timelineKey;
        }}
      >
        {timelineHours.map((hour) => {
          const condition = hour.weather.conditions.toLowerCase();
          const seaState = getSeaState(hour.waves, hour.wind);
          const icon = condition.includes('rain') || condition.includes('drizzle') ? 'grain' :
            condition.includes('cloud') || condition.includes('overcast') ? 'cloud' : 'wb-sunny';
          return (
            <Card key={hour.id} mode="outlined" style={styles.timelineCard}>
              <Card.Content style={styles.timelineCardContent}>
                <Paragraph style={styles.timelineTime}>{hourLabel(hour)}</Paragraph>
                <View style={styles.timelineWeather}>
                  <MaterialIcons name={icon} size={23} color={theme.colors.primary} />
                  <Paragraph style={styles.timelineTemp}>{hour.weather.temperature}°</Paragraph>
                </View>
                <Paragraph numberOfLines={1} style={styles.timelineCondition}>{hour.weather.conditions}</Paragraph>
                <Divider style={styles.timelineDivider} />
                <Paragraph style={styles.timelineLabel}>WIND</Paragraph>
                <Paragraph style={styles.timelineValue}>{hour.wind.speed} kt {direction(hour.wind.direction)}</Paragraph>
                <Paragraph style={styles.timelineSubValue}>G {hour.wind.gusts ?? hour.wind.speed} kt</Paragraph>
                <View style={styles.chopRow}>
                  <Paragraph style={styles.timelineLabel}>CHOP</Paragraph>
                  <View style={[styles.chopBadge, { backgroundColor: seaState.backgroundColor }]}>
                    <MaterialIcons name="waves" size={12} color={seaState.color} />
                    <Paragraph style={[styles.chopBadgeLabel, { color: seaState.color }]}>{seaState.label}</Paragraph>
                  </View>
                </View>
                <Paragraph style={styles.timelineLabel}>TOTAL SEAS</Paragraph>
                <Paragraph style={styles.timelineValue}>{hasMarineData(hour.waves) ? `${hour.waves.height} ft @ ${hour.waves.period || '—'}s` : '—'}</Paragraph>
                <Paragraph style={styles.timelineLabel}>SWELL</Paragraph>
                <Paragraph style={styles.timelineValue}>{hour.waves.swellHeight !== undefined ? `${hour.waves.swellHeight} ft @ ${hour.waves.swellPeriod ?? '—'}s` : '—'}</Paragraph>
                <Paragraph style={styles.sourceLabel}>{hour.waves.dataStatus === 'observed' ? 'NDBC OBSERVED' : hasMarineData(hour.waves) ? 'MODELED' : 'NO MARINE COVERAGE'}</Paragraph>
              </Card.Content>
            </Card>
          );
        })}
      </ScrollView>
      <View style={styles.disclaimer}>
        <MaterialIcons name="info-outline" size={16} color={theme.colors.onSurfaceVariant} />
        <Paragraph style={styles.disclaimerText}>Planning guidance only—not a go/no-go determination. Model data via Open-Meteo; observations via NOAA NDBC. Review official marine forecasts, warnings, tides, radar, and your vessel’s limits before departure.</Paragraph>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { marginTop: 24 },
  sectionHeading: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  title: { fontSize: 20, fontWeight: '700' },
  subtitle: { fontSize: 12, opacity: 0.65, marginTop: 2 },
  dateChips: { gap: 8, paddingBottom: 14 },
  dateChip: { marginRight: 8 },
  assessmentCard: { borderLeftWidth: 5, borderRadius: 12 },
  assessmentTop: { flexDirection: 'row', alignItems: 'flex-start' },
  statusDot: { width: 10, height: 10, borderRadius: 5, marginTop: 5, marginRight: 10 },
  assessmentCopy: { flex: 1 },
  statusLabel: { fontSize: 11, letterSpacing: 0.8, fontWeight: '700' },
  assessmentSummary: { fontSize: 14, lineHeight: 20, marginTop: 2 },
  metricRow: { flexDirection: 'row', marginTop: 18, gap: 8 },
  metric: { flex: 1 },
  metricLabel: { fontSize: 9, letterSpacing: 0.5, opacity: 0.55, fontWeight: '700' },
  metricValue: { fontSize: 12, fontWeight: '700', marginTop: 3 },
  assessmentDivider: { marginVertical: 12 },
  factor: { fontSize: 12, lineHeight: 18, marginBottom: 2 },
  timelineHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 22, marginBottom: 8 },
  hourlyTitle: { fontSize: 18, fontWeight: '700' },
  timelineSubtitle: { fontSize: 11, opacity: 0.6, marginTop: 1 },
  timelineScroll: { gap: TIMELINE_CARD_GAP, paddingBottom: 2 },
  timelineCard: { width: TIMELINE_CARD_WIDTH, borderRadius: 12 },
  timelineCardContent: { paddingHorizontal: 12, paddingVertical: 12 },
  timelineTime: { fontSize: 13, fontWeight: '700' },
  timelineWeather: { flexDirection: 'row', alignItems: 'center', marginTop: 10, gap: 6 },
  timelineTemp: { fontSize: 20, fontWeight: '700' },
  timelineCondition: { fontSize: 11, opacity: 0.72, marginTop: 5 },
  timelineDivider: { marginVertical: 10 },
  timelineLabel: { fontSize: 9, letterSpacing: 0.6, fontWeight: '700', opacity: 0.55, marginTop: 7 },
  timelineValue: { fontSize: 12, fontWeight: '700', marginTop: 2 },
  timelineSubValue: { fontSize: 10, opacity: 0.62, marginTop: 2 },
  sourceLabel: { fontSize: 8, letterSpacing: 0.5, fontWeight: '700', opacity: 0.48, marginTop: 8 },
  chopRow: { alignItems: 'stretch', marginTop: 9 },
  chopBadge: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4, borderRadius: 6, marginTop: 4, paddingHorizontal: 6, paddingVertical: 4 },
  chopBadgeLabel: { fontSize: 10, fontWeight: '700' },
  disclaimer: { flexDirection: 'row', gap: 8, alignItems: 'flex-start', marginTop: 14, marginBottom: 4 },
  disclaimerText: { flex: 1, fontSize: 11, lineHeight: 16, opacity: 0.72 },
});
